/* El reloj del hub es UTC-3, no el de la máquina del que mira.
 *
 * Igna (01/09): «necesito que el servidor donde está el hub esté en UTC-3».
 * El hub no tiene servidor con reloj —es estático en Cloudflare—, así que el
 * huso vive ACÁ: todo lo que muestra o calcula una fecha lo hace en hora
 * argentina, venga de donde venga el navegador. Sin esto, el mismo timestamp
 * decía una hora en el mail del bot (que formatea en UTC-3) y otra para
 * alguien que abre el hub desde otro huso — y «hoy» cambiaba de día a las
 * 21:00 si alguien calculaba con toISOString().
 *
 * UTC-3 FIJO Y NO `America/Argentina/Buenos_Aires`, por la doctrina que ya
 * está escrita en `shared/puerto.js` y en los crones del bot: hoy dan lo
 * mismo, y con la zona nombrada un cambio de huso legal movería horarios sin
 * que nadie tocara nada.
 *
 * Las dos trampas que este módulo mata:
 *  · `new Date().toISOString()` para «hoy» → después de las 21:00 AR da el
 *    día SIGUIENTE. Acá: `hoyAr()`.
 *  · `new Date("2026-08-31")` (fecha sin hora) → medianoche UTC, que en AR
 *    son las 21:00 del día ANTERIOR; mostrada, resta un día. Acá:
 *    `diaAr()` no pasa por Date para lo que ya es un día escrito.
 */

const TZ_AR_MS = -3 * 3600 * 1000;
const p2 = (n) => String(n).padStart(2, "0");

/* El mismo instante, corrido para leerlo con getUTC* como hora argentina.
 * NO cambia el instante guardado: cambia cómo se lee. */
export function enAR(iso) {
  if (iso === null || iso === undefined || iso === "") return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + TZ_AR_MS);
}

/* «Hoy» en la Argentina, como `YYYY-MM-DD` — para defaults de inputs date y
 * filtros. Es LA función que reemplaza a toISOString().slice(0, 10). */
export function hoyAr(ahora = new Date()) {
  const d = enAR(ahora);
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;
}

/* Un día YA ESCRITO («2026-08-31», o un ISO con hora) → «31/08/2026».
 * Lo date-only se corta como texto, sin pasar por Date: pasarlo restaría
 * un día. Lo que trae hora es un instante y se convierte a AR. */
export function diaAr(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10)) && s.length <= 10) {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  const d = enAR(s);
  if (!d) return "";
  return `${p2(d.getUTCDate())}/${p2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/* Un instante (ISO de Supabase) → «31/08/2026 21:35», en hora argentina. */
export function diaHoraAr(iso) {
  const d = enAR(iso);
  if (!d) return "";
  return `${p2(d.getUTCDate())}/${p2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ` +
         `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

/* Sólo la hora: «21:35». */
export function horaAr(iso) {
  const d = enAR(iso);
  if (!d) return "";
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}

/* El día argentino de un valor, como «YYYY-MM-DD» — para agrupar, comparar
 * y armar claves. Lo date-only ya está escrito; el instante se lee en AR. */
export function diaIsoAr(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10)) && s.length <= 10) {
    return s.slice(0, 10);
  }
  const d = enAR(s);
  return d ? `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}` : "";
}

/* «Ahora» para los helpers que cuentan días con el truco del mediodía local:
 * un Date al mediodía del día argentino de hoy. Con `new Date()` a secas, el
 * «hoy» de esas cuentas era el de la máquina del que mira. */
export function mediodiaHoyAr() {
  return new Date(hoyAr() + "T12:00:00");
}

/* Lo que tipeó el usuario en un input date/datetime-local → lo que se guarda.
 *
 * El día escrito viaja como TEXTO: `new Date("2026-08-31")` es medianoche
 * UTC, y guardado así el día vuelve corrido para cualquiera que lo lea al
 * oeste de Greenwich — el input lo mostraba un día antes y al re-guardar la
 * fecha se corría de verdad. El datetime-local es hora argentina escrita:
 * se le clava el -03:00 para que el instante guardado sea exactamente ése,
 * lo mire quien lo mire. */
export function fechaParaGuardar(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  if (valor instanceof Date) return valor;
  const s = String(valor).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?$/.test(s)) {
    return new Date(s.replace(" ", "T") + "-03:00");
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ¿Esa fecha de carga NO tiene hora de verdad?
 *
 * El vale de urea trae día, mes y año, sin hora. El bot guarda el vale de
 * otro día a las 12:00:00 en punto, para que no se corra de día por la zona.
 * Una carga real no cae nunca en el segundo exacto del mediodía, así que eso
 * es la marca de «no se sabe a qué hora». Nico, 01/10: el hub mostraba las
 * 316 cargas a las «12:00 p. m.», una hora inventada mostrada como dato. Es
 * la misma regla que `urea.sin_hora` en el bot. */
export function sinHora(iso) {
  const d = enAR(iso);
  if (!d) return false;
  return d.getUTCHours() === 12 && d.getUTCMinutes() === 0 &&
         d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

/* «30/09 14:32», o «30/09 · s/hora» si no tiene hora real. Corto, para tablas
 * donde el año sobra. */
export function diaHoraCorta(iso) {
  const d = enAR(iso);
  if (!d) return "";
  const dia = `${p2(d.getUTCDate())}/${p2(d.getUTCMonth() + 1)}`;
  return sinHora(iso) ? `${dia} · s/hora`
                      : `${dia} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}
